/* eslint-disable @typescript-eslint/no-explicit-any */
import Bottle from "bottlejs";
import OrgService from "@civicplus/preamble-ui/lib/Services/OrgService/OrgService";
import { isInRole } from "@civicplus/preamble-ui/lib/Utilities/User";
import { User } from "oidc-client-ts";
import { ApiService } from "../services/ApiService";
import { AuthService } from "../services/AuthService";
import { ConfigOptions } from "../entities/ConfigOptions";
import { FeatureFlags } from "../entities/FeatureFlags";
import { HcmsService } from "../services/HcmsService";
import { HistoryLogService } from "../services/HistoryLogService";
import { ManageSubscriptionsService } from "../services/ManageSubscriptionsService";
import { MessageService } from "../services/MessageService";
import { OrganizationService } from "../services/OrganizationService";
import { OrganizationSettingsService } from "../services/OrganizationSettingsService";
import { PendingMessageService } from "../services/PendingMessageService";
import { PermissionsService } from "../services/PermissionsService";
import { SubscriberImportService } from "../services/SubscriberImportService";
import { SubscribersService } from "../services/SubscribersService";
import { SubscriptionListsService } from "../services/SubscriptionListsService";
import { TemplateService } from "../services/TemplateService";
import { UserManager } from "../services/UserManager";
import { AxiosHttpProvider } from "./http/AxiosHttpProvider";
import { CacheProvider } from "./cache/cacheProvider";
import { WindowRouter } from "../shell/WindowRouter";
import { PrivacyService } from "../services/PrivacyService";
import { SuppressionsService } from "../services/SuppressionsService";
import { Organization, OrganizationDto } from "../entities/Organization";
import { UnsubscribeService } from "../services/UnsubscribeService";
import { ProductType } from "../entities/ProductType";
import { UserService } from "../services/UserService";

export interface OrgServiceContainer {
    orgId: string | null;
    orgService: OrgService | null;
    user: User | undefined;
    organization?: Organization & {
        hasAccess?: boolean;
        isOrgOwner?: boolean;
    };
}

declare module "bottlejs" {
    interface IContainer {
        ApiService: ApiService;
        AuthService: AuthService;
        CacheProvider: CacheProvider;
        ConfigOptions: ConfigOptions;
        FeatureFlags: FeatureFlags;
        HcmsService: HcmsService;
        HistoryLogService: HistoryLogService;
        ManageSubscriptionsService: ManageSubscriptionsService;
        MessageService: MessageService;
        PendingMessageService: PendingMessageService;
        PermissionsService: PermissionsService;
        PrivacyService: PrivacyService;
        OrganizationService: OrganizationService;
        OrganizationSettingsService: OrganizationSettingsService;
        OrgService: any;
        SubscriberImportService: SubscriberImportService;
        SubscribersService: SubscribersService;
        SubscriptionListsService: SubscriptionListsService;
        SuppressionsService: SuppressionsService;
        TemplateService: TemplateService;
        UnsubscribeService: UnsubscribeService;
        UserManager: UserManager;
        WindowRouter: WindowRouter;
    }
}

export let bottle: Bottle;

const init = () => {
    bottle = new Bottle();
    bottle.service("ApiService", ApiService, "HttpProvider", "WindowRouter");
    bottle.service("AuthService", AuthService, "ApiService");
    bottle.service("CacheProvider", CacheProvider);
    bottle.service("HcmsService", HcmsService, "ApiService");
    bottle.service("HistoryLogService", HistoryLogService, "ApiService");
    bottle.service("HttpProvider", AxiosHttpProvider);
    bottle.service("ManageSubscriptionsService", ManageSubscriptionsService);
    bottle.service("MessageService", MessageService, "ApiService");
    bottle.service("OrganizationService", OrganizationService, "ApiService", "CacheProvider");
    bottle.service("OrganizationSettingsService", OrganizationSettingsService, "ApiService");
    bottle.service("PendingMessageService", PendingMessageService, "ApiService");
    bottle.service("PermissionsService", PermissionsService, "ApiService");
    bottle.service("PrivacyService", PrivacyService, "ApiService");
    bottle.service("SubscriberImportService", SubscriberImportService, "ApiService", "UserManager");
    bottle.service("SubscribersService", SubscribersService, "ApiService");
    bottle.service("SubscriptionListsService", SubscriptionListsService, "ApiService");
    bottle.service("SuppressionsService", SuppressionsService, "ApiService");
    bottle.service("TemplateService", TemplateService, "ApiService");
    bottle.service("UnsubscribeService", UnsubscribeService, "ApiService");
    bottle.service("UserManager", UserManager, "AuthService", "WindowRouter");
    bottle.service("WindowRouter", WindowRouter);
    bottle.service("UserService", UserService, "ApiService");
};

// ApiService only throws the error message text, so a 401 can only be recognised from the axios message.
const isUnauthorizedError = (error: unknown): boolean => typeof error === "string" && /status code 401/i.test(error);

export const setupOrganizationService = async (orgIdOrName: string | null) => {
    if (!bottle.container.OrgService && orgIdOrName !== null) {
        let user: User | undefined = undefined;
        let orgDto: OrganizationDto | null = null;
        let orgService: OrgService | any | null = null;
        bottle.container.ApiService.orgId = orgIdOrName;

        const userManager: UserManager = bottle.container.UserManager;

        const isAuthenticated = await userManager.isAuthenticatedAsync(orgIdOrName);

        if (isAuthenticated) {
            user = await userManager.getUserAsync();

            const getOrgFunc = async (
                _page: number,
                _pageSize: number,
                _select: number,
                _order: string,
                search = ""
            ): Promise<{ items: Organization[]; total: number }> => {
                const service: OrganizationService = bottle.container.OrganizationService;
                const isSuperUser = await userManager.isSuperUser();

                if (search === "Select an Organization") {
                    search = "";
                }

                if (isSuperUser) {
                    const orgs = await service.searchOrgs(search);
                    if (!orgs) {
                        return {
                            items: [],
                            total: 0
                        };
                    }

                    return {
                        items: orgs.items,
                        total: orgs.items.length
                    };
                }

                const orgs = await service.getAssociatedOrgs(search);

                if (!orgs) {
                    return {
                        items: [],
                        total: 0
                    };
                }
                return {
                    items: orgs,
                    total: orgs.length
                };
            };

            const isInRoleFunc = (role: string): boolean => {
                return isInRole(role, user);
            };

            orgService = new OrgService({
                endpoint: userManager.getIdentityServerBaseUrl(),
                accessToken: user?.access_token || "",
                idToken: user?.id_token
            });
            orgService.GetUsersOrganizations = getOrgFunc;
            orgService.IsInRole = isInRoleFunc;
        }

        if (orgIdOrName) {
            const organizationService = bottle.container.OrganizationService as OrganizationService;

            if (isAuthenticated) {
                try {
                    orgDto = await organizationService.getOrganization();
                } catch (error) {
                    // Only a rejected token falls back to the public data. Anything else (500, network) must not
                    // silently demote a real admin to the citizen view.
                    if (!isUnauthorizedError(error)) {
                        throw error;
                    }
                    orgDto = null;
                }
            }

            if (!orgDto?.organization) {
                const citizenOrg = await organizationService.getCitizenOrganization();
                orgDto =
                    citizenOrg && typeof citizenOrg === "object"
                        ? {
                              organization: { ...citizenOrg, hcmsApp: null } as unknown as Organization,
                              hasAccess: false,
                              isOrgOwner: false
                          }
                        : null;
            }

            localStorage.setItem("organization:Notifications", orgIdOrName);
        }

        bottle.service("OrgService", function () {
            if (orgDto?.organization) {
                orgDto.organization.hasHcmsApp = orgDto?.organization.hcmsApp !== null;
                orgDto.organization.hasNotificationApp = orgDto?.organization.applications?.some(
                    (a) => a.productType === ProductType.Notifications
                );
            }

            return {
                orgId: orgIdOrName,
                orgService,
                user,
                organization: {
                    ...orgDto?.organization,
                    hasAccess: orgDto?.hasAccess,
                    isOrgOwner: orgDto?.isOrgOwner
                }
            };
        });
    }
};

export const setupFeatureFlags = async (orgIdOrName: string | null): Promise<void> => {
    if (orgIdOrName !== null) {
        const apiService = bottle.container.ApiService;

        if (!bottle.container.FeatureFlags) {
            const featureFlags = (await apiService.getNoOrg(`${orgIdOrName}/featureFlags`)) as FeatureFlags;

            bottle.service("FeatureFlags", function (): FeatureFlags {
                return {
                    ...featureFlags
                };
            });
        }
    }
};

export const setupConfigOptions = async (): Promise<void> => {
    const apiService = bottle.container.ApiService;

    if (!bottle.container.ConfigOptions) {
        const configOptions = (await apiService.getNoOrg("config")) as ConfigOptions;

        bottle.service("ConfigOptions", function (): ConfigOptions {
            return {
                ...configOptions
            };
        });
    }
};

export const reload = (): void => init();

init();
